import React from 'react';
import { Box, Button, Container, Stack, Typography } from '@mui/material';

/**
 * Auth bridge page — `/auth/callback`.
 *
 * Supabase auth links go: email button → `…supabase.co/auth/v1/verify` (validates
 * the token) → redirects to `redirect_to`. The mobile app sets `redirect_to` to
 * THIS url instead of the raw `mecfs://auth/callback` scheme, because that scheme
 * only opens on a phone with the app installed — on desktop / without the app it
 * dead-ends on a blank page.
 *
 * This page receives the verified result (implicit-flow tokens in the URL
 * *fragment*, PKCE `?code=` in the query, or `#error=…`), shows a branded
 * confirmation, and forwards the query + fragment verbatim to
 * `mecfs://auth/callback` so the app's Supabase client completes the session.
 * App side: MEInfoApp/src/domain/utils/AuthCallbackUrl.ts.
 *
 * Deliberately self-contained: it detects the recipient's language from `?lang`
 * or the browser (the APP user's language, which is independent of the website's
 * language context) and is not prerendered/SEO'd (served via the Netlify SPA
 * fallback, noindex).
 *
 * Deploy order (do NOT reorder):
 *   1. Deploy this website so `https://me-support.net/auth/callback` loads.
 *   2. Allow-list that URL in Supabase → Auth → URL Configuration → Redirect URLs.
 *   3. Only then ship the mobile app build — its `emailRedirectTo`/`redirectTo`
 *      already point here, but the value is baked into the bundle at build time.
 *      Shipping the app before steps 1–2 breaks confirmation links.
 */

// Fill in once the app is published. Empty → the store buttons stay hidden; the
// deep-link button still works.
const IOS_APP_URL = ''; // e.g. 'https://apps.apple.com/app/id0000000000'
const ANDROID_APP_URL = ''; // e.g. 'https://play.google.com/store/apps/details?id=net.mesupport.app'

const APP_SCHEME_URL = 'mecfs://auth/callback';

type Lang = 'de' | 'en';

const STRINGS: Record<Lang, Record<string, string>> = {
  de: {
    confirmHead: '✅ Erledigt',
    confirmMsg: 'Deine E-Mail ist bestätigt. Öffne die App, um fortzufahren.',
    recoveryHead: 'Passwort zurücksetzen',
    recoveryMsg: 'Öffne die App, um dein neues Passwort festzulegen.',
    errorHead: 'Link ungültig oder abgelaufen',
    errorMsg: 'Dieser Link ist nicht mehr gültig. Bitte fordere in der App eine neue E-Mail an.',
    open: 'App öffnen',
    hint: 'Öffnet sich die App nicht automatisch, tippe auf „App öffnen“. Auf dem Computer: öffne diesen Link auf deinem Handy.',
    ios: 'App Store',
    android: 'Google Play',
  },
  en: {
    confirmHead: '✅ All set',
    confirmMsg: 'Your email is confirmed. Open the app to continue.',
    recoveryHead: 'Reset password',
    recoveryMsg: 'Open the app to set your new password.',
    errorHead: 'Link invalid or expired',
    errorMsg: 'This link is no longer valid. Please request a new email in the app.',
    open: 'Open app',
    hint: 'If the app doesn’t open automatically, tap “Open app”. On a computer: open this link on your phone.',
    ios: 'App Store',
    android: 'Google Play',
  },
};

interface CallbackState {
  lang: Lang;
  isError: boolean;
  isRecovery: boolean;
  deepLink: string;
}

function readCallbackState(): CallbackState {
  const search = window.location.search; // '?…' or ''
  const hash = window.location.hash; // '#…' or ''

  // Forward query AND fragment verbatim: implicit-flow tokens live in the
  // fragment, the PKCE code in the query. The app merges both.
  const deepLink = APP_SCHEME_URL + search + hash;

  const merged = (search.replace(/^\?/, '') + '&' + hash.replace(/^#/, '')).replace(/^&+|&+$/g, '');
  const p = new URLSearchParams(merged);

  const isError = p.has('error') || p.has('error_code') || p.has('error_description');
  const isRecovery = p.get('type') === 'recovery';

  const langRaw = (p.get('lang') || navigator.language || 'de').toLowerCase();
  const lang: Lang = langRaw.indexOf('en') === 0 ? 'en' : 'de';

  return { lang, isError, isRecovery, deepLink };
}

export const AuthCallbackPage: React.FC = () => {
  // Read once on mount — the URL fragment/query is the whole input.
  const [{ lang, isError, isRecovery, deepLink }] = React.useState(readCallbackState);
  const t = STRINGS[lang];

  const heading = isError ? t.errorHead : isRecovery ? t.recoveryHead : t.confirmHead;
  const message = isError ? t.errorMsg : isRecovery ? t.recoveryMsg : t.confirmMsg;

  React.useEffect(() => {
    document.documentElement.lang = lang;

    // noindex — this is a transient, token-carrying URL.
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex';
    document.head.appendChild(meta);

    // Scrub the tokens from the visible address bar / history once captured
    // (deepLink already holds them). Fragments never reach the server.
    try {
      window.history.replaceState(null, '', window.location.pathname);
    } catch {
      /* ignore */
    }

    // Auto-attempt to open the app once (a phone with the app installed leaves
    // this page). Not on error, to avoid handing the app a broken link.
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (!isError) {
      timer = setTimeout(() => {
        window.location.href = deepLink;
      }, 400);
    }
    return () => {
      document.head.removeChild(meta);
      if (timer) clearTimeout(timer);
    };
  }, [lang, isError, deepLink]);

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 } }}>
      <Box
        sx={{
          maxWidth: 460,
          mx: 'auto',
          textAlign: 'center',
          borderRadius: 4,
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          bgcolor: 'background.paper',
        }}
      >
        <Box sx={{ px: 4, py: 3.5, bgcolor: isError ? 'error.main' : 'primary.main' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff' }}>
            {heading}
          </Typography>
        </Box>
        <Box sx={{ px: 4, py: 4 }}>
          <Typography variant="body1" sx={{ color: 'text.primary', mb: 3 }}>
            {message}
          </Typography>
          <Button
            href={deepLink}
            variant="contained"
            size="large"
            rel="nofollow"
            sx={{ fontWeight: 700, borderRadius: 3, px: 4, py: 1.5 }}
          >
            {t.open}
          </Button>
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 3 }}>
            {t.hint}
          </Typography>
          {(IOS_APP_URL || ANDROID_APP_URL) && (
            <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ mt: 2.5 }}>
              {IOS_APP_URL && (
                <Button href={IOS_APP_URL} target="_blank" rel="nofollow noopener" variant="outlined" size="small">
                  {t.ios}
                </Button>
              )}
              {ANDROID_APP_URL && (
                <Button href={ANDROID_APP_URL} target="_blank" rel="nofollow noopener" variant="outlined" size="small">
                  {t.android}
                </Button>
              )}
            </Stack>
          )}
        </Box>
      </Box>
    </Container>
  );
};
